import { c as _c } from "react/compiler-runtime";
import type { StaticImageData } from 'next/image';
import { knownDotcomUrls } from '@sola/my-sdk/constants.ts';
import type { JsonLink } from '@sola/schema/lib/contentful.ts';
import { getPlainTextFromRichTextBody } from '@sola/shared-utils/contentful/rich-text.ts';
import { usePositionedContent } from '../contexts/positioned-content.tsx';
import type { PositionedContentMap } from '../contexts/positioned-content.tsx';
import ctaImage from '../molecules/affinity-card-cta/STORYBOOK-child-holding-goat.jpg';
const getImageUrl = (image: string | StaticImageData): string => typeof image === 'string' ? image : image.src;
const ctaImageUrl = getImageUrl(ctaImage);
export type NoSponsorshipsCtaData = {
  ctaLabel: string;
  href: string;
  imageSrc: string;
  summary: string;
  title: string;
};
export const MOCK_POSITIONED_CONTENT = {
  myCtaSponsorChildren: {
    __typename: 'FlexibleContent',
    sys: {
      id: 'storybook-my-cta-sponsor-children',
      publishedAt: '2026-01-01T00:00:00.000Z'
    },
    title: 'Sponsor a child',
    body: {
      json: {
        nodeType: 'document',
        data: {},
        content: [{
          nodeType: 'paragraph',
          data: {},
          content: [{
            nodeType: 'text',
            value: 'Make your greatest impact through child sponsorship. For $43/month, you can help provide a child with consistent care.',
            marks: [],
            data: {}
          }]
        }]
      }
    },
    primaryCTA: {
      type: 'external',
      title: 'View Children',
      externalUrl: 'https://www.compassion.com/sponsor-a-child/'
    },
    media: {
      image: {
        url: ctaImageUrl,
        height: 800,
        width: 1200
      },
      altText: 'Child holding a goat'
    },
    linkedContentCollection: {
      items: []
    },
    referencedContentCollection: {
      items: []
    }
  }
} satisfies PositionedContentMap;
const getCtaHref = (cta: JsonLink | null | undefined): string | undefined => {
  if (!cta || cta.type === 'none') {
    return;
  }
  switch (cta.type) {
    case 'external':
      {
        return cta.externalUrl;
      }
    case 'anchor':
      {
        return cta.anchor;
      }
    case 'mailto':
      {
        return `mailto:${cta.mailto}`;
      }
    case 'tel':
      {
        return `tel:${cta.tel}`;
      }
    case 'internal':
      {
        const path = cta.entry?.path;
        const parameters = cta.parameters ? `?${cta.parameters}` : '';
        const anchor = cta.anchor ? `#${cta.anchor}` : '';
        return path ? `${path}${parameters}${anchor}` : anchor || undefined;
      }
    default:
      {
        return;
      }
  }
};
export const useNoSponsorshipsCta = () => {
  const $ = _c(8);
  const cmsContent = usePositionedContent("myCtaSponsorChildren");
  if (cmsContent?.__typename !== "FlexibleContent") {
    return null;
  }
  const cta = cmsContent.primaryCTA ?? cmsContent.primaryCta;
  const ctaLabel = cta?.type === "none" ? undefined : cta?.title;
  const href = knownDotcomUrls.sponsorAChild;
  const imageSrc = cmsContent.media?.image?.url;
  let t0;
  if ($[0] !== cmsContent.body || $[1] !== cmsContent.subtitle) {
    t0 = cmsContent.subtitle ?? getPlainTextFromRichTextBody(cmsContent.body);
    $[0] = cmsContent.body;
    $[1] = cmsContent.subtitle;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const summary = t0;
  const title = cmsContent.title;
  if (!ctaLabel || !href || !imageSrc || !summary || !title) {
    return null;
  }
  let t1;
  if ($[3] !== ctaLabel || $[4] !== imageSrc || $[5] !== summary || $[6] !== title) {
    t1 = {
      ctaLabel,
      href,
      imageSrc,
      summary,
      title
    };
    $[3] = ctaLabel;
    $[4] = imageSrc;
    $[5] = summary;
    $[6] = title;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};